// src/components/widget/inputs

import React, { useState, useEffect } from "react";
import { svgIcons, Icon } from '../images/Icon';
import styles from './Toggle.module.scss';

export type ToggleForm = "checkbox" | "radio button" | "bullet" | "switch";

export interface ToggleProps {
  id?: number;
  checked?: boolean;
  readOnly?: boolean;
  form?: ToggleForm;
  onChange?: (newState: boolean, id?: number) => void;
  width?: string;
  height?: string;
  fillColorOn?: string;
  fillColorOff?: string;
  // Props pour les icônes
  iconOn?: string;
  iconOff?: string;
  iconColorOn?: string;
  iconColorOff?: string;
  iconSize?: string;
  iconPositionLeft?: string;
  iconPositionRight?: string;
  iconTransformOn?: string;
  iconTransformOff?: string;
  // Props pour les textes
  textOn?: string;
  textOff?: string;
  textColorOn?: string;
  textColorOff?: string;
  textSize?: string;
  textFontWeight?: string;
  textPositionLeft?: string;
  textPositionRight?: string;
  textTransformOn?: string;
  textTransformOff?: string;
  // Contrôle de l'affichage
  show?: "none" | "text" | "icon";
  // internalPosition=true  → label DANS le SVG (position absolute, original)
  // internalPosition=false → label HORS du SVG : [label OFF][SVG][label ON] en flex
  internalPosition?: boolean;
  persistentIcons?: boolean;
  invertColor?: boolean;
}

export const Toggle = ({
  id,
  checked,
  readOnly = false,
  form = "checkbox",
  onChange,
  width = "26px",
  height = "26px",
  fillColorOn = "#25C400",
  fillColorOff = "#C30000",
  // Props pour les icônes
  iconOn,
  iconOff,
  iconColorOn = "black",
  iconColorOff = "black",
  iconSize,
  iconPositionLeft = "24%",
  iconPositionRight = "24%",
  iconTransformOn = "translate(25%, -50%)",
  iconTransformOff = "translate(-41%, -50%)",
  // Props pour les textes
  textOn = "ON",
  textOff = "OFF",
  textColorOn = "black",
  textColorOff = "black",
  textSize = "16px",
  textFontWeight = "normal",
  textPositionLeft = "24%",
  textPositionRight = "24%",
  textTransformOn = "translate(110%, -50%)",
  textTransformOff = "translate(-120%, -50%)",
  show = "none",
  internalPosition = true,
  persistentIcons = false,
  invertColor = false,
}: ToggleProps) => {
  const [state, setState] = useState(checked);

  useEffect(() => {
    setState(checked);
  }, [checked]);

  // Taille effective de l'icône :
  // - mode interne : % relatif au SVG parent (défaut 25%)
  // - mode externe : valeur absolue nécessaire car la div label n'a pas de taille de référence (défaut 20px)
  const effectiveIconSize = iconSize ?? (internalPosition ? "25%" : height);

  const getCurrentFillColor = () => {
    const boolState       = Boolean(state);
    const boolInvertColor = Boolean(invertColor);
    return boolInvertColor
      ? (boolState ? fillColorOff : fillColorOn)
      : (boolState ? fillColorOn  : fillColorOff);
  };

  const handleClick = () => {
    if (!readOnly && onChange) {
      onChange(!state, id);
      setState(!state);
    }
  };

  // ── Rendu d'un label (icône SVG ou texte) ──────────────────────────────────

  const renderLabel = (
    content: string | undefined | null,
    color: string,
    isIconMode: boolean,
    extraStyle?: React.CSSProperties,
  ) => {
    if (!content) return null;

    if (isIconMode && svgIcons[content]) {
      return React.cloneElement(svgIcons[content], {
        style: {
          width:  effectiveIconSize,
          height: effectiveIconSize,
          color,
          fill:   color,
          stroke: color,
          ...extraStyle,
        },
      });
    }

    return (
      <span style={{
        color,
        fontSize:   textSize,
        fontWeight: textFontWeight as React.CSSProperties['fontWeight'],
        userSelect: 'none' as React.CSSProperties['userSelect'],
        whiteSpace: 'nowrap',
        ...extraStyle,
      }}>
        {content}
      </span>
    );
  };

  // ── Helpers label courant / labels persistants ────────────────────────────

  const isIconMode = show === "icon";
  const isOn       = Boolean(state);

  const getCurrentLabel = () => {
    if (show === "none") return { content: null, isIcon: false, color: "" };
    const content = isOn ? (isIconMode ? iconOn  : textOn)  : (isIconMode ? iconOff  : textOff);
    const color   = isOn ? (isIconMode ? iconColorOn : textColorOn) : (isIconMode ? iconColorOff : textColorOff);
    const isIcon  = Boolean(isIconMode && content && svgIcons[content as string]);
    return { content, isIcon, color };
  };

  const getPersistentLabel = (forOn: boolean) => {
    if (show === "none") return { content: null, isIcon: false, color: "" };
    const content = forOn ? (isIconMode ? iconOn  : textOn)  : (isIconMode ? iconOff  : textOff);
    const color   = forOn ? (isIconMode ? iconColorOn : textColorOn) : (isIconMode ? iconColorOff : textColorOff);
    const isIcon  = Boolean(isIconMode && content && svgIcons[content as string]);
    return { content, isIcon, color };
  };

  // ── Mode internalPosition=true : label DANS le SVG (position absolute) ─────
  // Comportement original conservé à l'identique.

  const renderInternalLabel = () => {
    const { content, isIcon, color } = getCurrentLabel();
    if (!content) return null;

    const position  = isOn
      ? (isIcon ? iconPositionRight : textPositionRight)
      : (isIcon ? iconPositionLeft  : textPositionLeft);
    const transform = isOn
      ? (isIcon ? iconTransformOn   : textTransformOn)
      : (isIcon ? iconTransformOff  : textTransformOff);

    return (
      <div style={{
        position:       'absolute',
        top:            '40%',
        left:           !isOn ? position : undefined,
        right:          isOn  ? position : undefined,
        transform,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        pointerEvents:  'none',
      }}>
        {renderLabel(content as string, color, isIcon)}
      </div>
    );
  };


  // ── Mode internalPosition=false : [label OFF] [SVG] [label ON] en flex ─────
  // CORRECTION : les labels sont des éléments frères du SVG dans un flex row.
  // La largeur totale = label OFF + SVG + label ON. Aucun débordement possible.

  const renderExternalLabels = () => {
    if (show === "none") return { left: null, right: null };

    const offLabel = getPersistentLabel(false);
    const onLabel  = getPersistentLabel(true);

    // Sans persistentIcons : seul le label de l'état courant est affiché,
    // l'autre côté est une div vide (évite les sauts de largeur).
    // Avec persistentIcons : les deux sont affichés, l'inactif à 0.3 opacité.

    const showOffContent = persistentIcons || !isOn;
    const showOnContent  = persistentIcons || isOn;
    const offFaded       = persistentIcons && isOn;
    const onFaded        = persistentIcons && !isOn;

    // Les div de labels ont la même hauteur que le SVG
    // → align-items: center sur .switch_row aligne tout sur le même axe horizontal.
    const labelHeightStyle: React.CSSProperties = { height };

    const leftNode = (
      <div className={styles.switch_label_left} style={labelHeightStyle}>
        {showOffContent
          ? renderLabel(
              offLabel.content as string,
              offLabel.color,
              offLabel.isIcon,
              offFaded ? { opacity: 0.3 } : undefined,
            )
          : null}
      </div>
    );

    const rightNode = (
      <div className={styles.switch_label_right} style={labelHeightStyle}>
        {showOnContent
          ? renderLabel(
              onLabel.content as string,
              onLabel.color,
              onLabel.isIcon,
              onFaded ? { opacity: 0.3 } : undefined,
            )
          : null}
      </div>
    );

    return { left: leftNode, right: rightNode };
  };

  // ── Rendu principal du switch ─────────────────────────────────────────────

  const renderSwitch = () => {
    const currentFillColor = getCurrentFillColor();

    // Le dessin SVG du switch est centré dans le viewBox "0 0 29 23.43"
    // (yMin=4.8, yMax=18.43 → hauteur réelle ≈ 13.63, centrée sur y=11.617).
    // On utilise preserveAspectRatio="xMidYMid meet" et un viewBox recadré
    // pour que le dessin occupe exactement le carré width×height, centré.
    const svgNode = (
      <div style={{
        width,
        height,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        flexShrink:      0,
        cursor:          readOnly ? 'default' : 'pointer',
      }}
        onClick={handleClick}
      >
        <svg
          fill={currentFillColor}
          width={width}
          height={height}
          // viewBox recadré sur le dessin réel : x0=0, y0≈4.8, w=24, h≈13.64
          viewBox="0 4.8 24 13.64"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: 'block' }}
          role="switch"
          aria-checked={state}
        >
          {state ? (
            <path d="m24 11.617c0 3.763-3.05 6.814-6.813 6.817h-10.371c-3.765 0-6.817-3.052-6.817-6.817s3.052-6.817 6.817-6.817h10.366c3.763 0 6.814 3.05 6.817 6.813zm-6.817-4.545c-2.507.001-4.54 2.034-4.54 4.542s2.033 4.542 4.542 4.542 4.542-2.033 4.542-4.542c0-1.252-.507-2.386-1.327-3.208-.822-.824-1.959-1.334-3.215-1.334-.001 0-.001 0-.002 0z" />
          ) : (
            <path d="m0 11.617c0-3.763 3.05-6.814 6.813-6.817h10.371c3.765 0 6.817 3.052 6.817 6.817s-3.052 6.817-6.817 6.817h-10.366c-3.763 0-6.814-3.05-6.817-6.813zm6.817 4.543c2.507-.002 4.539-2.035 4.539-4.542 0-2.509-2.034-4.542-4.542-4.542s-4.542 2.034-4.542 4.542c0 1.254.508 2.389 1.329 3.21.822.823 1.958 1.332 3.214 1.332h.003z" />
          )}
        </svg>
      </div>
    );

    // ── internalPosition=true : label dans le SVG (comportement original) ──
    if (internalPosition) {
      return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          {svgNode}
          {renderInternalLabel()}
          {/* Labels persistants internes */}
          {persistentIcons && show !== "none" && (() => {
            const onLabel  = getPersistentLabel(true);
            const offLabel = getPersistentLabel(false);
            return (
              <>
                {!isOn && onLabel.content && (
                  <div style={{
                    position:       'absolute',
                    top:            '40%',
                    right:          isIconMode ? iconPositionRight : textPositionRight,
                    transform:      isIconMode ? iconTransformOn   : textTransformOn,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    pointerEvents:  'none',
                    opacity:        0.3,
                  }}>
                    {renderLabel(onLabel.content as string, onLabel.color, onLabel.isIcon)}
                  </div>
                )}
                {isOn && offLabel.content && (
                  <div style={{
                    position:       'absolute',
                    top:            '40%',
                    left:           isIconMode ? iconPositionLeft : textPositionLeft,
                    transform:      isIconMode ? iconTransformOff : textTransformOff,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    pointerEvents:  'none',
                    opacity:        0.3,
                  }}>
                    {renderLabel(offLabel.content as string, offLabel.color, offLabel.isIcon)}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      );
    }

    // ── internalPosition=false : [label OFF] [SVG] [label ON] en flex ──────
    // Les labels sont HORS du SVG, dans le flux normal. Pas de position absolute,
    // pas de transform qui déborde. La largeur totale est la somme des 3 éléments.
    const { left, right } = renderExternalLabels();

    return (
      <div className={styles.switch_row}>
        {left}
        {svgNode}
        {right}
      </div>
    );
  };

  const renderInput = () => {
    switch (form) {
      case "checkbox":
        return <input type="checkbox" checked={state} onChange={handleClick} aria-checked={state} disabled={readOnly} />;
      case "radio button":
        return <input type="radio"    checked={state} onChange={handleClick} aria-checked={state} disabled={readOnly} />;
      case "bullet":
        // Le halo fait partie de l'aspect d'une puce dans cette application : les colonnes
        // qui en dessinent une à la main l'obtiennent par un drop-shadow de 6px à 80 %
        // d'opacité (cf. .statusDotOk / .statusDotNok des lignes de liste). Il est appliqué
        // ici pour que `form="bullet"` produise la même chose sans que l'appelant ait à
        // l'envelopper. La couleur suit celle de la puce, donc les états intermédiaires
        // (orange, violet) gardent un halo assorti.
        return (
          <span style={{
            display: 'inline-flex',
            filter:  `drop-shadow(0 0 6px color-mix(in srgb, ${getCurrentFillColor()} 80%, transparent))`,
          }}>
            <Icon name="disk" color={getCurrentFillColor()} width={20} height={16} hoverable={false} clickable={false} />
          </span>
        );
      case "switch":
        return renderSwitch();
      default:
        return null;
    }
  };

  return <>{renderInput()}</>;
};

export default Toggle;